import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Spinner, Badge, Tabs, Tab, Table, Button, Modal } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { FaMedal, FaStar, FaCommentDots, FaShieldAlt, FaBell, FaEdit, FaTrash, FaEye, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import useCommunityNotifications from '../../../hooks/useCommunityNotifications';
import { formatTimeAgo } from '../../utils/communityUtils';

export default function MyCommunityDashboard() {
  const { currentUser, token } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  
  // We still use this hook for the global unread count badge
  const unreadCount = useCommunityNotifications();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [loadingProfile, setLoadingProfile] = useState(true);
  
  // My Posts state
  const [myDiscussions, setMyDiscussions] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  
  // Notifications state
  const [fullNotifications, setFullNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  // Delete modal state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // 1. Fetch Profile Data
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoadingProfile(true);
        const res = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/profile/${currentUser.uid}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success) {
          setProfile(data.profile);
        }
      } catch (error) {
        console.error("Error fetching community profile", error);
      } finally {
        setLoadingProfile(false);
      }
    };
    fetchProfile();
  }, [currentUser, token]);

  // 2. Fetch My Posts when tab is active
  useEffect(() => {
    if (activeTab === 'posts') {
      const fetchPosts = async () => {
        try {
          setLoadingPosts(true);
          const res = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/my-discussions`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = await res.json();
          if (data.success) {
            setMyDiscussions(data.discussions || []);
          }
        } catch (error) {
          console.error("Error fetching my discussions", error);
        } finally {
          setLoadingPosts(false);
        }
      };
      fetchPosts();
    }
  }, [activeTab, token]);

  // 3. Fetch Full Notifications when tab is active
  useEffect(() => {
    if (activeTab === 'notifications') {
      const fetchNotifs = async () => {
        try {
          setLoadingNotifications(true);
          const res = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/notifications`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = await res.json();
          if (data.success) {
            setFullNotifications(data.notifications || []);
          }
        } catch (error) {
          console.error("Error fetching notifications", error);
        } finally {
          setLoadingNotifications(false);
        }
      };
      fetchNotifs();
    }
  }, [activeTab, token]);

  // Mark notification read handler
  const handleMarkRead = async (id, link) => {
    try {
      await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/notifications/${id}/read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // Optimistically update local state
      setFullNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
      if (link) navigate(link);
    } catch (err) {
      console.error("Failed to mark read", err);
      if (link) navigate(link);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/notifications/read-all`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setFullNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    } catch (err) {
      console.error("Failed to mark all read", err);
    }
  };

  // Delete handlers
  const confirmDelete = (discussion) => {
    setDeleteTarget(discussion);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_URL || ''}/api/community/discussions/${deleteTarget.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setMyDiscussions(prev => prev.filter(d => d.id !== deleteTarget.id));
        setShowDeleteModal(false);
        setDeleteTarget(null);
      } else {
        alert(data.error || 'Failed to delete');
      }
    } catch (err) {
      alert('Network error. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  if (loadingProfile) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
        <Spinner animation="border" variant="warning" />
      </div>
    );
  }

  const isVerifiedOperator = profile?.verifiedOperatorStatus?.status === 'approved';
  const pendingVerification = profile?.verifiedOperatorStatus?.status === 'pending';

  const getStatusBadge = (status) => {
    switch (status) {
      case 'published': return <Badge bg="success">Published</Badge>;
      case 'held_for_review': return <Badge bg="warning" text="dark">Pending Review</Badge>;
      case 'rejected': return <Badge bg="danger">Rejected</Badge>;
      case 'deleted': return <Badge bg="secondary">Deleted</Badge>;
      default: return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'reply': return <FaCommentDots className="text-primary fs-4" />;
      case 'discussion_approved': return <FaCheckCircle className="text-success fs-4" />;
      case 'discussion_rejected': return <FaExclamationTriangle className="text-danger fs-4" />;
      default: return <FaBell className="text-secondary fs-4" />;
    }
  };

  return (
    <div className="py-4 community-dashboard-container">
      <h2 className="mb-4">Community Control Center</h2>

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4 community-tabs"
        variant="underline"
      >
        <Tab eventKey="overview" title="Overview">
          <Row>
            <Col md={8}>
              {/* Main Status Card */}
              <Card className="mb-4 shadow-sm border-0">
                <Card.Body>
                  <div className="d-flex align-items-center gap-4 mb-4">
                    <div 
                      className="rounded-circle d-flex align-items-center justify-content-center bg-secondary text-white"
                      style={{ width: '80px', height: '80px', fontSize: '30px' }}
                    >
                      {profile?.displayName ? profile.displayName.charAt(0).toUpperCase() : 'U'}
                    </div>
                    <div>
                      <h4 className="mb-1">{profile?.displayName || 'BBR User'}</h4>
                      <div className="d-flex gap-3 text-muted">
                        <span><FaStar className="text-warning me-1" /> {profile?.reputation || 0} Rep</span>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex gap-2 mb-3 flex-wrap">
                    {(profile?.communityRoles || []).map((role, idx) => (
                      <Badge key={idx} bg="primary">{role}</Badge>
                    ))}
                  </div>

                  <div className="d-flex gap-3">
                    <Link to={`/community/user/${currentUser.uid}`} className="btn btn-outline-secondary btn-sm">
                      View Public Profile
                    </Link>
                    <Link to="/community" className="btn btn-warning btn-sm">
                      Go to Community Hub
                    </Link>
                  </div>
                </Card.Body>
              </Card>

              {/* Quick Actions */}
              <Card className="shadow-sm border-0 mb-4">
                <Card.Header className="bg-white fw-bold">Quick Links</Card.Header>
                <Card.Body>
                  <Row>
                    <Col sm={6} className="mb-3">
                      <Link to="/community/new" className="text-decoration-none text-dark">
                        <div className="p-3 border rounded text-center hover-bg-light transition">
                          <FaCommentDots size={24} className="text-primary mb-2 d-block mx-auto" />
                          <strong>Start a Discussion</strong>
                        </div>
                      </Link>
                    </Col>
                    <Col sm={6} className="mb-3">
                      <Link to="/dashboard/community/trust-levels" className="text-decoration-none text-dark">
                        <div className="p-3 border rounded text-center hover-bg-light transition">
                          <FaMedal size={24} className="text-warning mb-2 d-block mx-auto" />
                          <strong>Trust & Badges</strong>
                        </div>
                      </Link>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              {currentUser.role !== 'Admin' && (
                <Card className="shadow-sm border-warning">
                  <Card.Header className="bg-warning text-dark fw-bold d-flex align-items-center">
                    <FaShieldAlt className="me-2" /> Verified Operator Status
                  </Card.Header>
                  <Card.Body>
                    {isVerifiedOperator ? (
                      <>
                        <div className="text-center mb-3">
                          <FaMedal size={40} className="text-warning mb-2" />
                          <h5>Active Status: <span className="text-success fw-bold">{profile?.verifiedOperatorStatus?.level || 'Bronze'}</span></h5>
                          <p className="text-muted small">You have access to the private operator forum.</p>
                        </div>
                        <Link to="/dashboard/community/operator-forum" className="btn btn-primary w-100 mb-2">
                          Enter Private Forum
                        </Link>
                      </>
                    ) : pendingVerification ? (
                      <div className="text-center py-3">
                        <Spinner animation="grow" variant="warning" size="sm" className="mb-3" />
                        <h6 className="fw-bold">Application Under Review</h6>
                        <p className="text-muted small mb-0">Our team is reviewing your documents. This usually takes 1-2 business days.</p>
                      </div>
                    ) : (
                      <div className="text-center py-3">
                        <p className="text-muted small mb-3">
                          Get verified to increase renter trust, unlock premium badges, and access the private operator forum.
                        </p>
                        <Link to="/dashboard/community/apply-operator" className="btn btn-outline-warning w-100 fw-bold">
                          Apply for Verification
                        </Link>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        </Tab>

        {/* My Posts Tab */}
        <Tab eventKey="posts" title="My Posts">
          <Card className="shadow-sm border-0">
            <Card.Body className="p-0">
              {loadingPosts ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="warning" />
                </div>
              ) : myDiscussions.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <p className="mb-3">You haven't posted any discussions yet.</p>
                  <Button variant="warning" onClick={() => navigate('/community/new')}>
                    Start a Discussion
                  </Button>
                </div>
              ) : (
                <Table responsive hover className="mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th>Status</th>
                      <th>Title</th>
                      <th>Category</th>
                      <th className="text-center">Replies</th>
                      <th className="text-center">Views</th>
                      <th>Posted</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myDiscussions.map(d => (
                      <tr key={d.id} className="align-middle">
                        <td>{getStatusBadge(d.status)}</td>
                        <td className="text-truncate" style={{ maxWidth: '250px' }}>
                          <span title={d.title}>{d.title}</span>
                          {d.status === 'rejected' && d.rejectionReason && (
                            <div className="small text-danger mt-1 text-truncate" title={d.rejectionReason}>
                              Reason: {d.rejectionReason}
                            </div>
                          )}
                        </td>
                        <td className="small text-muted">{d.communityId}</td>
                        <td className="text-center">{d.replyCount || 0}</td>
                        <td className="text-center">{d.viewCount || 0}</td>
                        <td className="small text-muted">{formatTimeAgo(d.createdAt)}</td>
                        <td className="text-end">
                          <Button
                            variant="outline-info"
                            size="sm"
                            className="me-2"
                            onClick={() => navigate(`/community/thread/${d.id}`)}
                            title="View post"
                          >
                            <FaEye />
                          </Button>
                          {d.status === 'rejected' && (
                            <Button
                              variant="warning"
                              size="sm"
                              className="me-2"
                              onClick={() => navigate(`/community/edit/${d.id}`)}
                              title="Edit and Resubmit"
                            >
                              <FaEdit /> Fix
                            </Button>
                          )}
                          {d.status !== 'deleted' && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => confirmDelete(d)}
                              title="Delete post"
                            >
                              <FaTrash />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* Notifications Tab */}
        <Tab 
          eventKey="notifications" 
          title={
            <span>
              Notifications {unreadCount > 0 && <Badge bg="danger" className="ms-2">{unreadCount}</Badge>}
            </span>
          }
        >
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-white d-flex justify-content-between align-items-center fw-bold">
              <h5 className="mb-0">Recent Activity</h5>
              {fullNotifications.some(n => !n.isRead) && (
                <Button variant="outline-primary" size="sm" onClick={handleMarkAllRead}>
                  Mark All as Read
                </Button>
              )}
            </Card.Header>
            <Card.Body className="p-0">
              {loadingNotifications ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="warning" />
                </div>
              ) : fullNotifications.length === 0 ? (
                <div className="text-center text-muted py-5">
                  <FaBell className="fs-1 text-secondary mb-3" />
                  <p>You have no notifications yet.</p>
                </div>
              ) : (
                <div className="notification-list">
                  {fullNotifications.map(notif => (
                    <div 
                      key={notif.id} 
                      className={`d-flex align-items-start p-3 border-bottom transition cursor-pointer ${!notif.isRead ? 'bg-light fw-bold' : 'hover-bg-light'}`}
                      onClick={() => handleMarkRead(notif.id, notif.link)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="me-3 mt-1">
                        {getNotificationIcon(notif.type)}
                      </div>
                      <div className="flex-grow-1">
                        <div className={`mb-1 ${!notif.isRead ? 'text-dark' : 'text-dark'}`}>
                          {notif.message}
                        </div>
                        {notif.metadata?.rejectionReason && (
                          <div className="small text-danger bg-danger bg-opacity-10 p-2 rounded mb-2">
                            "{notif.metadata.rejectionReason}"
                          </div>
                        )}
                        <div className="small text-muted">
                          {formatTimeAgo(notif.createdAt)}
                        </div>
                      </div>
                      {!notif.isRead && (
                        <div className="ms-3 align-self-center">
                          <div className="bg-primary rounded-circle" style={{ width: '10px', height: '10px' }}></div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Delete Confirmation Modal for My Posts Tab */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Body className="text-center py-5 px-4">
          <FaTrash style={{ fontSize: '3rem', color: '#dc3545' }} className="mb-4" />
          <h4 className="mb-3">Delete this post?</h4>
          <p className="text-muted mb-4">
            This action cannot be undone. Your post will be permanently removed.
          </p>
          <div className="d-flex gap-3 justify-content-center">
            <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? <Spinner size="sm" className="me-2" /> : <FaTrash className="me-2" />}
              Yes, Delete It
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
